import { Translations } from '~/app/i18n/i18n';
import { MarketFormatter } from '~/app/market-config/market-formatter';
import getTranslatedLabel from '~/app/utils/get-tranlslated-label';
import {
	ConsumptionAndEmissionValuePerEnergyManagementWltp,
	EecValuePerEnergyManagementWltp,
	FuelFloatValueFragment,
	RangeInfoFragment,
	RangeLevelInfo,
	Remark,
	TechnicalDataFloatItemFragment,
	TechnicalDataItemWithKey,
	TechnicalDataRangeItemFragment,
	TechnicalDataTextItemFragment,
} from '~/graphql/__generated__/graphql';

export type DataSection = { title: string; entries: DataSectionEntry[] };

export type TechDataSection = { title: string; entries: TechDataSectionEntry[] };

export type DataSectionEntry = {
	title: string;
	value: string;
	remarks?: (Remark | null)[] | null | undefined;
};

export type TechDataSectionEntry = {
	title: string;
	value: string;
	remarks?: (Remark | null)[] | null | undefined;
	min?: string;
	max?: string;
	unit?: string;
};

export type TechnicalEntry =
	| TechnicalDataFloatItemFragment
	| TechnicalDataTextItemFragment
	| RangeInfoFragment
	| TechnicalDataRangeItemFragment
	| FuelFloatValueFragment
	| RangeLevelInfo;

export function createEntry(obj: TechnicalEntry | null | undefined): DataSectionEntry | null {
	if (!obj || !obj.label) return null;

	switch (obj.__typename) {
		case 'TechnicalDataFloatItem':
		case 'TechnicalDataTextItem':
			return {
				title: obj.label,
				value: obj.formattedValue,
				remarks: obj.remarks,
			};

		case 'RangeInfo':
		case 'RangeLevelInfo':
			if (obj.min === obj.max) {
				return {
					title: obj.label,
					value: `${obj.max} ${obj.unit || ''}`,
					remarks: obj.remarks,
				};
			}

			return {
				title: obj.label,
				value: `${obj.max}-${obj.min} ${obj.unit || ''}`,
				remarks: obj.remarks,
			};
		case 'TechnicalDataRangeItem':
		case 'FuelFloatValue':
			return {
				title: obj.label,
				value: obj.formattedValue as string,
			};

		default:
			return null;
	}
}

export function createConsumptionRangeEntry(
	obj: RangeInfoFragment | null | undefined,
	formatter: MarketFormatter,
): DataSectionEntry | null {
	if (!obj?.label) return null;

	return {
		title: obj.label,
		value:
			obj.min === obj.max
				? `${formatter.formatConsumption(obj.max)} ${obj.unit || ''}`
				: `${formatter.formatConsumption(obj.max)}-${formatter.formatConsumption(obj.min)} ${obj.unit || ''}`,
		remarks: obj.remarks,
	};
}

/**
 * Creates an entry for a consumption value
 */
export function createConsumptionEntry(
	obj: TechnicalDataFloatItemFragment | null | undefined,
	formatter: MarketFormatter,
): DataSectionEntry | null {
	if (!obj?.label) return null;

	return {
		title: obj.label,
		value: `${formatter.formatConsumption(obj.value)} ${obj.unit || ''}`,
		remarks: obj.remarks,
	};
}

/**
 * Creates an entry for an emission value.
 */
export function createEmissionRangeEntry(obj: RangeInfoFragment | null | undefined): DataSectionEntry | null {
	if (!obj) return null;

	return {
		title: obj.label,
		value: obj.min === obj.max ? `${obj.max} ${obj.unit || ''}` : `${obj.max}-${obj.min} ${obj.unit || ''}`,
		remarks: obj.remarks,
	};
}

export function createEmissionSummary(
	obj: ConsumptionAndEmissionValuePerEnergyManagementWltp | null | undefined,
): DataSectionEntry | null {
	if (!obj) return null;

	return {
		title: obj.label as string,
		value: `${obj.value} ${obj.unit || ''}`,
		remarks: obj.remarks,
	};
}

export function createEECSummaryEntry(
	obj: EecValuePerEnergyManagementWltp | null | undefined,
): DataSectionEntry | null {
	if (!obj) return null;

	// Separate the formattedValue to label and value
	const valueRegex = /:\s*([^:\s]+)$/;
	const match = obj.formattedValue?.match(valueRegex);

	if (match) {
		const value = match[1];
		const label = obj.formattedValue?.replace(valueRegex, '');

		// Replace the value and the footnote placeholder with an empty string
		const trimmedLabel = label?.trim().replace(/\{\{.*?\}\}/g, '');

		return { title: trimmedLabel || '', value };
	}

	return null;
}

export function createDataSection(
	title: string,
	entries: (DataSectionEntry | '' | null | undefined | false)[],
): DataSection {
	return {
		title,
		entries: filterConditionalEntries(entries),
	};
}

export function createNewDataSection(
	title: string,
	entries: (TechnicalDataItemWithKey | '' | null | undefined | false)[],
	translations: Translations,
): TechDataSection {
	return {
		title,
		entries: filterConditionalEntries(
			mapEntriesToTechSectionEntry(filterConditionalEntries(entries), translations),
		),
	};
}

export function appendDataSection(title: string, value: string) {
	return [
		{
			title,
			value,
			remarks: [],
		},
	];
}

/** Filters out entries of type `null | undefined | false`. */
export function filterConditionalEntries<T>(entries: (T | '' | null | undefined | false)[]): Array<NonNullable<T>> {
	return entries.filter(Boolean) as Array<NonNullable<T>>;
}

export function mapEntriesToTechSectionEntry(
	entries: TechnicalDataItemWithKey[],
	translations: Translations,
): Array<TechDataSectionEntry | null> {
	const newEntries = entries.map((entry) => {
		const label = getTranslatedLabel(translations, entry.item.translationKey, entry.item.label);
		if (label) {
			const mappedEntry: TechDataSectionEntry = {
				title: getTranslatedLabel(translations, entry.item.translationKey, entry.item.label),
				value: entry.item.formattedValue!,
				remarks: entry.item.remarks,
			};
			return mappedEntry;
		}

		return null;
	});

	return newEntries;
}
